import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink, RecursiveMenu } from '@uncinc/react-drupal-core';
import type { MenuItemLinkElementProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';

import EmailAccountPage from './Email';
import BillingAccountPage from './Billing';
import OverviewAccountPage from './Overview';
import PersonalAccountPage from './Personal';
import ServicesAccountPage from './Services';
import AddressesAccountPage from './Addresses';
import onAccountNavMenuClick from '../helpers';
import { Hamburger } from '../../../components';
import { APP_OPTIONS, DEFAULT_LANGUAGE } from '../../../config/constants';
import useCurrentUser from '../../../hooks/useCurrentUser';

import './style.scss';

const SideBarLinkElement: React.FC<MenuItemLinkElementProps> = ({
  title,
  url,
  onClickFunction,
}) => {
  const className = classNames('RecursiveMenu__title', {
    hasLink: Boolean(url),
    is_active: url === window.location.pathname,
  });

  return (
    <CoreLink
      href={url}
      className={className}
      onClick={onClickFunction}
    >
      {title}
    </CoreLink>
  );
};

const UserAccountPage: React.FC = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const { tab } = useParams<{ tab: string }>();

  const { profile } = user ?? {};
  const {
    field_first_name: fieldFirstName = null,
    field_last_name: fieldLastName = null,
  } = profile ?? {};

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuUsernameRef = useRef<HTMLDivElement>(null);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const toggleMobileMenuIsOpen = () => {
    mobileMenuRef.current?.classList.toggle('menuIsOpen');
    menuUsernameRef.current?.classList.toggle('menuIsOpen');
    setMobileMenuIsOpen((state) => !state);
  };

  const onRecursiveMenuClick = (
    e: React.MouseEvent<React.HTMLAttributes<HTMLDivElement>, MouseEvent>,
  ) => {
    onAccountNavMenuClick(history, dispatch, e);
  };

  const {
    accountPagePersonalTab,
    accountPageOverviewTab,
    accountPageEmailTab,
    accountPageBillingTab,
    accountPageAddressesTab,
    accountPageServicesTab,
  } = APP_OPTIONS;

  const tabConfig = {
    [accountPagePersonalTab]: {
      icon: 'icon-user',
      component: PersonalAccountPage,
      title: t('mijn_vnl.personal.title'),
    },
    [accountPageOverviewTab]: {
      icon: 'icon-contact',
      component: OverviewAccountPage,
      title: t('mijn_vnl.overview.title'),
    },
    [accountPageEmailTab]: {
      icon: 'icon-mail-thin',
      component: EmailAccountPage,
      title: t('mijn_vnl.email.title'),
      subtitle: t('mijn_vnl.email.subtitle'),
    },
    [accountPageBillingTab]: {
      icon: 'icon-notebook',
      component: BillingAccountPage,
      title: t('mijn_vnl.billing.title'),
    },
    [accountPageAddressesTab]: {
      icon: 'icon-home',
      component: AddressesAccountPage,
      title: t('mijn_vnl.addresses.title'),
    },
    [accountPageServicesTab]: {
      icon: 'icon-grid',
      component: ServicesAccountPage,
      title: t('mijn_vnl.services.title'),
    },
  };

  const InnerComponent = tabConfig[tab]!.component;

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{tabConfig[tab]?.title}</title>
      </Helmet>
      <div className="Relative">
        <div className="GreenFiller" />
        <Container className="AccountPage">
          <div className="AccountPage__desktopSidebar col-4">
            <div className="AccountPage__desktopSidebar__wrapper">
              <RecursiveMenu
                menuId="account"
                MenuItemLinkElement={SideBarLinkElement}
                onClick={onRecursiveMenuClick}
              />
            </div>
          </div>
          <div className="AccountPage__mobileSidebar col-12">
            <div ref={menuUsernameRef} className="AccountPage__mobileSidebar__username">
              <Hamburger
                setShowCartPopup={false}
                OpenMobileMenu={mobileMenuIsOpen}
                handleClick={toggleMobileMenuIsOpen}
              />
              {
                user && fieldFirstName && (
                  <span>
                    {fieldFirstName[0]?.value} {
                      fieldLastName && fieldLastName[0]?.value
                        ? fieldLastName[0].value
                        : ''
                    }
                  </span>
                )
              }
            </div>
            <div ref={mobileMenuRef} className="AccountPage__mobileSidebar__menuWrapper">
              <RecursiveMenu
                menuId="account"
                onClick={onRecursiveMenuClick}
                onClickFunction={toggleMobileMenuIsOpen}
                MenuItemLinkElement={SideBarLinkElement}
              />
            </div>
          </div>
          <div className="AccountPage__inner col-12 col-lg-8">
            <div className="AccountPage__inner__title">
              <div className="AccountPage__inner__title__icon">
                <i className={classNames('icon icon-variant-1', tabConfig[tab]?.icon)} />
              </div>
              <h1>{tabConfig[tab]?.title}</h1>
            </div>
            {
              tabConfig[tab]?.subtitle &&
              <div className="AccountPage__inner__subtitle">{tabConfig[tab]?.subtitle}</div>
            }
            <InnerComponent />
          </div>
        </Container>
        <div className="WhiteFiller" />
      </div>
    </>
  );
};

export default React.memo(UserAccountPage);
